const DAY_MS = 24 * 60 * 60 * 1000;

const toDateOnly = (value) => {
  if (!value) return null;

  const raw = value?.toDate ? value.toDate() : value;
  if (raw instanceof Date) {
    if (Number.isNaN(raw.getTime())) return null;
    return new Date(Date.UTC(raw.getFullYear(), raw.getMonth(), raw.getDate()));
  }

  const text = String(raw).trim();
  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return new Date(
      Date.UTC(
        Number(isoMatch[1]),
        Number(isoMatch[2]) - 1,
        Number(isoMatch[3]),
      ),
    );
  }

  const ukMatch = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (ukMatch) {
    return new Date(
      Date.UTC(Number(ukMatch[3]), Number(ukMatch[2]) - 1, Number(ukMatch[1])),
    );
  }

  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(
    Date.UTC(
      parsed.getUTCFullYear(),
      parsed.getUTCMonth(),
      parsed.getUTCDate(),
    ),
  );
};

const addYears = (date, amount) => {
  const next = new Date(date.getTime());
  next.setUTCFullYear(next.getUTCFullYear() + amount);
  return next;
};

const clamp = (value, minimum, maximum) =>
  Math.min(maximum, Math.max(minimum, value));

const formatHours = (value) =>
  Number(value || 0).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const formatDate = (value) =>
  [
    String(value.getUTCDate()).padStart(2, "0"),
    MONTH_LABELS[value.getUTCMonth()],
    value.getUTCFullYear(),
  ].join(" ");

const getLatestReportDate = (allCpd, iddCpd) => {
  const dates = [allCpd?.reportDate, iddCpd?.reportDate]
    .map(toDateOnly)
    .filter(Boolean)
    .sort((left, right) => right.getTime() - left.getTime());
  return dates[0] || null;
};

const getMortgageCycle = ({ asOf, appointmentDate }) => {
  const year = asOf.getUTCFullYear();
  const thisApril = new Date(Date.UTC(year, 3, 1));
  const cycleStart =
    asOf >= thisApril ? thisApril : new Date(Date.UTC(year - 1, 3, 1));
  const cycleEnd = new Date(Date.UTC(cycleStart.getUTCFullYear() + 1, 3, 1));
  const appointed = toDateOnly(appointmentDate);

  if (!appointed) {
    return {
      error:
        "Date Appointed is missing, so the pro-rated requirement cannot be calculated.",
    };
  }

  const effectiveStart = appointed > cycleStart ? appointed : cycleStart;
  const cycleDays = (cycleEnd - cycleStart) / DAY_MS;
  const elapsedDays = clamp((asOf - effectiveStart) / DAY_MS, 0, cycleDays);

  return {
    start: effectiveStart,
    end: cycleEnd,
    displayStart: cycleStart,
    displayEnd: new Date(cycleEnd.getTime() - DAY_MS),
    progress: elapsedDays / cycleDays,
  };
};

const getFinancialPlannerCycle = ({ asOf, spsExpiryDate }) => {
  const storedExpiry = toDateOnly(spsExpiryDate);
  if (!storedExpiry) {
    return {
      error:
        "The SPS Certificate expiry date is missing, so the CPD year cannot be calculated.",
    };
  }

  let cycleEnd = new Date(
    Date.UTC(
      asOf.getUTCFullYear(),
      storedExpiry.getUTCMonth(),
      storedExpiry.getUTCDate(),
    ),
  );

  while (cycleEnd < asOf) cycleEnd = addYears(cycleEnd, 1);
  const cycleStart = addYears(cycleEnd, -1);
  const cycleDays = Math.max(1, (cycleEnd - cycleStart) / DAY_MS);
  const elapsedDays = clamp((asOf - cycleStart) / DAY_MS, 0, cycleDays);

  return {
    start: cycleStart,
    end: cycleEnd,
    displayStart: cycleStart,
    displayEnd: cycleEnd,
    progress: elapsedDays / cycleDays,
  };
};

export const calculateCpdProgress = ({
  allCpd,
  iddCpd,
  isFinancialPlanner,
  adviserType,
  appointmentDate,
  spsExpiryDate,
}) => {
  if (!allCpd || !iddCpd) {
    return {
      cpdStatus: "Incomplete",
      cpdStatusColor: "grey",
      cpdStatusTooltip:
        "Both the All CPD and IDD reports are required to assess progress.",
    };
  }

  const asOf = getLatestReportDate(allCpd, iddCpd);
  if (!asOf) {
    return {
      cpdStatus: "Incomplete",
      cpdStatusColor: "grey",
      cpdStatusTooltip:
        "A valid report date is required to assess CPD progress.",
    };
  }

  const cycle = isFinancialPlanner
    ? getFinancialPlannerCycle({ asOf, spsExpiryDate })
    : getMortgageCycle({ asOf, appointmentDate });

  if (cycle.error) {
    return {
      cpdStatus: "Incomplete",
      cpdStatusColor: "grey",
      cpdStatusTooltip: cycle.error,
    };
  }

  const structuredTarget = isFinancialPlanner ? 21 : 15;
  const iddTarget = 15;
  const isGiProtection = adviserType === "GI/Prot";
  const targets = {
    total: isFinancialPlanner ? 35 : isGiProtection ? 15 : 24,
    structured: structuredTarget,
    idd: iddTarget,
  };
  const expectedToDate = {
    total: targets.total * cycle.progress,
    structured: targets.structured * cycle.progress,
    idd: targets.idd * cycle.progress,
  };
  const structuredDone = Number(allCpd.structuredMinutes || 0) / 60;
  const iddDone = Number(iddCpd.cpdMinutes || 0) / 60;
  const totalDone = structuredDone + iddDone;
  const actual = {
    total: totalDone,
    structured: structuredDone,
    idd: iddDone,
  };
  const onTrack =
    actual.total >= expectedToDate.total &&
    actual.structured >= expectedToDate.structured &&
    actual.idd >= expectedToDate.idd;
  const remainingStructured = Math.max(
    0,
    targets.structured - actual.structured,
  );
  const remainingIdd = Math.max(0, targets.idd - actual.idd);
  const remaining = {
    total: remainingStructured + remainingIdd,
    structured: remainingStructured,
    idd: remainingIdd,
  };
  const displayAdviserType = adviserType || (isFinancialPlanner ? "FP" : "MA");

  return {
    cpdStatus: onTrack ? "On Track" : "Behind",
    cpdStatusColor: onTrack ? "green" : "red",
    cpdStatusDetails: {
      adviserType: displayAdviserType,
      timeline: `${formatDate(cycle.displayStart)} to ${formatDate(cycle.displayEnd)}`,
      expected: targets,
      done: actual,
      remaining,
    },
    cpdStatusTooltip: [
      displayAdviserType,
      ...(isFinancialPlanner
        ? [
            // `SPS timeline: ${formatDate(cycle.displayStart)} to ${formatDate(cycle.displayEnd)}.`,
          ]
        : []),
      `CPD timeline: ${formatDate(cycle.displayStart)} to ${formatDate(cycle.displayEnd)}.`,
      `Expected: Total ${formatHours(targets.total)}h | Structured ${formatHours(targets.structured)}h | IDD ${formatHours(targets.idd)}h.`,
      `Done: Total ${formatHours(actual.total)}h | Structured ${formatHours(actual.structured)}h | IDD ${formatHours(actual.idd)}h.`,
      `Remaining: Total ${formatHours(remaining.total)}h | Structured ${formatHours(remaining.structured)}h | IDD ${formatHours(remaining.idd)}h.`,
    ].join("\n"),
  };
};
